import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Tag, 
  RefreshCw, 
  Info, 
  AlertCircle, 
  CheckCircle2, 
  Calendar,
  Hash,
  Search,
  X,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { getAllReleaseVersions } from "../../../../service/apiService";

interface ReleaseVersion {
  id: string;
  sequence_number: number;
  version: string;
  notes: string;
  released_at: string;
}

enum LoadingState {
  IDLE = 'idle',
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error'
}

const useReleaseVersions = () => {
  const [releaseVersions, setReleaseVersions] = useState<ReleaseVersion[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.LOADING);
  const [error, setError] = useState<string | null>(null);

  const fetchReleaseVersions = useCallback(async () => {
    try {
      setLoadingState(LoadingState.LOADING);
      setError(null);
      
      const data = await getAllReleaseVersions();
      const sortedData = data.sort((a, b) => b.sequence_number - a.sequence_number);
      
      setReleaseVersions(sortedData);setLoadingState(LoadingState.SUCCESS);
    } catch (err: any) {
      console.error("Error fetching release versions:", err);
      
      const errorMessage = err?.response?.status === 404
        ? "No release versions found" 
        : "Failed to load release version information";
        
      setError(errorMessage);
      setLoadingState(LoadingState.ERROR);
    }
  }, []);

  return {
    releaseVersions,
    loadingState,
    error,
    fetchReleaseVersions,
    isLoading: loadingState === LoadingState.LOADING,
    isEmpty: loadingState === LoadingState.SUCCESS && releaseVersions.length === 0
  };
};

const formatDate = (dateString: string): string => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  } catch {
    return dateString;
  }
};

const getRelativeTime = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffInSeconds < 60) return'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return formatDate(dateString);
  } catch {
    return dateString;
  }
};

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-4animate-pulse">
    <div className="h-6 bg-gray-200 rounded w-1/4"></div>
    {[...Array(4)].map((_, index) => (
      <div key={index} className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-start space-x-4">
          <div className="w-20 h-8 bg-gray-200 rounded"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

interface ErrorMessageProps {
  title: string;
  message: string;
  onRetry?: () => void;
}

const ErrorMessage: React.FC<ErrorMessageProps> = ({ title, message, onRetry }) => (
  <div className="bg-red-50 border border-red-200 rounded-xl p-6">
    <div className="flex items-start space-x-4">
      <AlertCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-red-800">{title}</h4>
        <p className="mt-1 text-sm text-red-700">{message}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="mt-3 inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 transition-colors duration-200"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" />
            Try Again
          </button>
        )}
      </div>
    </div>
  </div>
);

interface VersionBadgeProps {
  version: string;
  isLatest: boolean;
  releaseDate: string;
  sequence: number;
}

const VersionBadge: React.FC<VersionBadgeProps> = ({ 
  version, 
  isLatest, 
  releaseDate, 
  sequence 
}) => (
  <div className={`p-4 rounded-xl border-2 transition-all duration-200 ${
    isLatest 
      ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 shadow-sm' 
      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
  }`}>
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center space-x-2">
        <Tag className={`h-5 w-5 ${isLatest ? 'text-green-600' : 'text-gray-600'}`} />
        <span className={`text-lg font-bold ${isLatest ? 'text-green-700' : 'text-gray-900'}`}>
          {version}
        </span>
        {isLatest && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800"><CheckCircle2 className="h-3 w-3 mr-1" />
            Latest
          </span>
        )}
      </div>
    </div>
    
    <div className="flex items-center space-x-4text-xs text-gray-600">
      <div className="flex items-center space-x-1">
        <Calendar className="h-3 w-3" />
        <span>{getRelativeTime(releaseDate)}</span>
      </div>
      <div className="flex items-center space-x-1">
        <Hash className="h-3 w-3" />
        <span>v{sequence}</span>
      </div>
    </div>
  </div>
);

// Release Notes Component
interface ReleaseNotesProps {
  notes: string;
  version: string;
  isExpanded: boolean;
  onToggle: () => void;
}

const ReleaseNotes: React.FC<ReleaseNotesProps> = ({ 
  notes, 
  version, 
  isExpanded, 
  onToggle 
}) => {
  const shouldShowToggle = notes && notes.length > 200;
  const displayNotes = shouldShowToggle && !isExpanded 
    ? notes.substring(0, 200) + "..." 
    : notes;

  if (!notes) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Info className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p className="text-sm">No release notes available for {version}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="prose prose-sm max-w-none">
        <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
          {displayNotes}
        </div>
      </div>
      
      {shouldShowToggle && (
        <button
          onClick={onToggle}
          className="mt-4 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" />
              Show Less
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-1" />
              Read More
            </>
          )}
        </button>
      )}
    </div>
  );
};

// Search and Filter Component
interface SearchFilterProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  totalCount: number;
  filteredCount: number;
}

const SearchFilter: React.FC<SearchFilterProps> = ({
  searchTerm,
  onSearchChange,
  totalCount,
  filteredCount
}) => (
  <div className="relative">
    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
      <Search className="h-4 w-4 text-gray-400" />
    </div>
    <input
      type="text"
      placeholder="Search versions or release notes..."
      value={searchTerm}
      onChange={(e) => onSearchChange(e.target.value)}
      className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
    />
    {searchTerm && (
      <>
        <button
          onClick={() => onSearchChange('')}
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
        >
          <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
        </button>
        <div className="mt-2 text-xs text-gray-500">
          Showing {filteredCount} of {totalCount} versions
        </div>
      </>
    )}
  </div>
);

// Empty State Component
const EmptyState: React.FC = () => (
  <div className="text-center py-16">
    <div className="bg-gray-100 rounded-full p-6 w-24 h-24 mx-auto mb-4flex items-center justify-center">
      <Tag className="h-10 w-10 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Release Versions</h3>
    <p className="text-gray-600max-w-md mx-auto">
      No release information is currently available. Check back later or contact your administrator.
    </p>
  </div>
);

// Main Component
const VersionManagement: React.FC = () => {
  const {
    releaseVersions, 
    loadingState, 
    error, 
    fetchReleaseVersions, 
    isLoading, 
    isEmpty 
  } = useReleaseVersions();

  const [searchTerm, setSearchTerm] = useState('');
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchReleaseVersions();
  }, [fetchReleaseVersions]);

  const filteredVersions = useMemo(() => {
    if (!searchTerm.trim()) return releaseVersions;
    
    const term = searchTerm.toLowerCase();
    return releaseVersions.filter(version =>
      version.version.toLowerCase().includes(term) ||
      version.notes.toLowerCase().includes(term)
    );
  }, [releaseVersions, searchTerm]);

  // Toggle expanded state for release notes
  const toggleExpanded = useCallback((versionId: string) => {
    setExpandedVersions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(versionId)) {
        newSet.delete(versionId);
      } else {
        newSet.add(versionId);
      }
      return newSet;
    });
  }, []);

  // Render loading state
  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center space-x-3mb-8">
          <div className="h-6 w-6 bg-blue-200 rounded animate-pulse" />
          <div className="h-6 w-48 bg-gray-200 rounded animate-pulse" />
        </div>
        <LoadingSkeleton />
      </div>
    );
  }

  // Render error state
  if (loadingState === LoadingState.ERROR) {
    return (
      <div className="p-8">
        <div className="flex items-center space-x-3 mb-8">
          <Tag className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-bold text-gray-900">Release Versions</h2>
        </div>
        <ErrorMessage
          title="Unable to Load Versions"
          message={error || "An unexpected error occurred"}
          onRetry={fetchReleaseVersions}
        />
      </div>
    );
  }

  // Render empty state
  if (isEmpty) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <Tag className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Release Versions</h2>
          </div>
          <button
            onClick={fetchReleaseVersions}
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
        <EmptyState />
      </div>
    );
  }

  // Main render
  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Tag className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Release Versions</h2>
            <p className="text-sm text-gray-600mt-1">
              {filteredVersions.length} {filteredVersions.length === 1 ? 'version' : 'versions'} available
            </p>
          </div>
        </div><button
          onClick={fetchReleaseVersions}
          disabled={isLoading}
          className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg border transition-all duration-200 ${
            isLoading
              ? 'border-gray-200 text-gray-400 cursor-not-allowed'
              : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
          }`}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          {isLoading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Search Section */}
      {releaseVersions.length > 3 && (
        <div className="mb-8">
          <SearchFilter
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            totalCount={releaseVersions.length}
            filteredCount={filteredVersions.length}
          />
        </div>
      )}

      {/* Versions List */}
      <div className="space-y-8">
        {filteredVersions.map((version, index) => (
          <div key={version.id} className="group">
            {/* Version Header */}
            <VersionBadge
              version={version.version}
              isLatest={index === 0}
              releaseDate={version.released_at}
              sequence={version.sequence_number}
            />
            
            {/* Release Notes */}
            <div className="mt-4">
              <ReleaseNotes
                notes={version.notes}
                version={version.version}
                isExpanded={expandedVersions.has(version.id)}
                onToggle={() => toggleExpanded(version.id)}
              />
            </div>
          </div>
        ))}
      </div>

      {/* No Search Results */}
      {searchTerm && filteredVersions.length === 0 && (
        <div className="text-center py-12">
          <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No versions found</h3>
          <p className="text-gray-600">
            Try adjusting your search terms or{' '}
            <button
              onClick={() => setSearchTerm('')}
              className="text-blue-600 hover:text-blue-500 font-medium"
            >
              clear search
            </button>
          </p>
        </div>
      )}
    </div>
  );
};

export default VersionManagement;